import { useMemo } from "react";

import { defineMessage, useIntl } from "react-intl";

import { useLocalizedHref } from "@/libraries/web-framework";
import { useLocalizedPartnerdirectory } from "@/libraries/web-framework/src/localization/use-localized-partnerdirectory";
import { ProductKeyTypes } from "@/libraries/wpl/foundations/src";

import { MobileNavCardProps } from "./mobile-nav-card";
import { MobileNavMenuPage, LinkSection } from "./mobile-nav-menu-page";

export const MobileNavResources = ({
    productKey = "home"
}: {
    productKey?: ProductKeyTypes;
}) => {
    const intl = useIntl();

    function useResourcesData(): Array<MobileNavCardProps> {
        const partnerdirectoryHref = useLocalizedPartnerdirectory();

        return useMemo(() => {
            return [
                {
                    key: "customer-support",
                    href: "//support.atlassian.com/",
                    name: intl.formatMessage(
                        defineMessage({
                            id: "globalnavv2.subnav.resources.customer-support.title",
                            defaultMessage: "Customer Support"
                        })
                    ),
                    description: intl.formatMessage(
                        defineMessage({
                            id: "globalnavv2.subnav.resources.customer-support.content",
                            defaultMessage:
                                "Ask questions, report bugs & give us feedback"
                        })
                    )
                },
                {
                    key: "find-partners",
                    href: partnerdirectoryHref,
                    name: intl.formatMessage(
                        defineMessage({
                            id: "globalnavv2.subnav.resources.find-partners.title",
                            defaultMessage: "Find Partners"
                        })
                    ),
                    description: intl.formatMessage(
                        defineMessage({
                            id: "globalnavv2.subnav.resources.find-partners.content",
                            defaultMessage:
                                "Consulting, training & product customization support"
                        })
                    )
                },
                {
                    key: "migration",
                    href: "/migration",
                    name: intl.formatMessage(
                        defineMessage({
                            id: "globalnavv2.subnav.resources.migration-program.title",
                            defaultMessage: "Migration Program"
                        })
                    ),
                    description: intl.formatMessage(
                        defineMessage({
                            id: "globalnavv2.subnav.resources.migration-program.content",
                            defaultMessage:
                                "Step-by-step guidance for your Atlassian cloud migration"
                        })
                    )
                },
                {
                    key: "university",
                    href: "//university.atlassian.com/",
                    name: intl.formatMessage(
                        defineMessage({
                            id: "globalnavv2.subnav.resources.university.title",
                            defaultMessage: "University"
                        })
                    ),
                    description: intl.formatMessage(
                        defineMessage({
                            id: "globalnavv2.subnav.resources.university.content",
                            defaultMessage:
                                "Learn and expand your skill set for all things Atlassian"
                        })
                    )
                }
            ];
        }, [partnerdirectoryHref]);
    }

    function useResourcesLinks(): Array<LinkSection> {
        return [
            {
                key: "support",
                name: intl.formatMessage(
                    defineMessage({
                        id: "globalnavv2.subnav.resources.links.support",
                        defaultMessage: "Support"
                    })
                ),
                links: [
                    {
                        key: "general-inquiries",
                        text: intl.formatMessage(
                            defineMessage({
                                id: "globalnavv2.subnav.resources.links.general-inquiries",
                                defaultMessage: "General Inquiries"
                            })
                        ),
                        href: useLocalizedHref(
                            "/company/contact/general-inquiries"
                        )
                    },
                    {
                        key: "technical-support",
                        text: intl.formatMessage(
                            defineMessage({
                                id: "globalnavv2.subnav.resources.links.technical-support",
                                defaultMessage: "Technical Support"
                            })
                        ),
                        href: "//support.atlassian.com/contact/"
                    },
                    {
                        key: "product-advice",
                        text: intl.formatMessage(
                            defineMessage({
                                id: "globalnavv2.subnav.resources.links.product-advice",
                                defaultMessage: "Product Advice"
                            })
                        ),
                        href: useLocalizedHref(
                            "/company/contact/product-evaluator-advice"
                        )
                    },
                    {
                        key: "pricing-billing",
                        text: intl.formatMessage(
                            defineMessage({
                                id: "globalnavv2.subnav.resources.links.pricing-billing",
                                defaultMessage: "Pricing and Billing"
                            })
                        ),
                        href: useLocalizedHref(
                            "/company/contact/purchasing-licensing"
                        )
                    },
                    {
                        key: "partner-support",
                        text: intl.formatMessage(
                            defineMessage({
                                id: "globalnavv2.subnav.resources.links.partner-support",
                                defaultMessage: "Partner Support"
                            })
                        ),
                        href: useLocalizedHref("/partners")
                    },
                    {
                        key: "developer-support",
                        text: intl.formatMessage(
                            defineMessage({
                                id: "globalnavv2.subnav.resources.links.developer-support",
                                defaultMessage: "Developer Support"
                            })
                        ),
                        href: "//developer.atlassian.com/"
                    },
                    {
                        key: "enterprise-support",
                        text: intl.formatMessage(
                            defineMessage({
                                id: "globalnavv2.subnav.resources.links.enterprise-support",
                                defaultMessage: "Enterprise Support"
                            })
                        ),
                        href: useLocalizedHref("/enterprise/services")
                    },
                    {
                        key: "purchasing-licensing",
                        text: intl.formatMessage(
                            defineMessage({
                                id: "globalnavv2.subnav.resources.links.purchasing-billing",
                                defaultMessage: "Purchasing and Licensing"
                            })
                        ),
                        href: useLocalizedHref("/licensing/purchase-licensing")
                    }
                ]
            },
            {
                key: "learn",
                name: intl.formatMessage(
                    defineMessage({
                        id: "globalnavv2.subnav.resources.links.learn",
                        defaultMessage: "Learn"
                    })
                ),
                links: [
                    {
                        key: "project-management",
                        text: intl.formatMessage(
                            defineMessage({
                                id: "globalnavv2.subnav.resources.links.project-management",
                                defaultMessage: "Project Management"
                            })
                        ),
                        href: useLocalizedHref(
                            "/work-management/project-management"
                        )
                    },
                    {
                        key: "project-collaboration",
                        text: intl.formatMessage(
                            defineMessage({
                                id: "globalnavv2.subnav.resources.links.project-collaboration",
                                defaultMessage: "Project Collaboration"
                            })
                        ),
                        href: useLocalizedHref(
                            "/work-management/project-collaboration"
                        )
                    },
                    {
                        key: "agile",
                        text: intl.formatMessage(
                            defineMessage({
                                id: "globalnavv2.subnav.resources.links.agile",
                                defaultMessage: "Agile"
                            })
                        ),
                        href: useLocalizedHref("/agile")
                    },
                    {
                        key: "team-playbook",
                        text: intl.formatMessage(
                            defineMessage({
                                id: "globalnavv2.subnav.resources.links.team-playbook",
                                defaultMessage: "Team Playbook"
                            })
                        ),
                        href: useLocalizedHref("/team-playbook")
                    },
                    {
                        key: "community",
                        text: intl.formatMessage(
                            defineMessage({
                                id: "globalnavv2.subnav.resources.links.community",
                                defaultMessage: "Community"
                            })
                        ),
                        href: useLocalizedHref("/community")
                    },
                    {
                        key: "product-documentation",
                        text: intl.formatMessage(
                            defineMessage({
                                id: "globalnavv2.subnav.resources.links.product-documentation",
                                defaultMessage: "Product Documentation"
                            })
                        ),
                        href: "//confluence.atlassian.com/display/ALLDOC/Atlassian+Documentation"
                    },
                    {
                        key: "get-started",
                        text: intl.formatMessage(
                            defineMessage({
                                id: "globalnavv2.subnav.resources.links.get-started",
                                defaultMessage: "Get Started"
                            })
                        ),
                        href: useLocalizedHref("/get-started")
                    }
                ]
            }
        ];
    }

    const resourcesData = useResourcesData();
    const resourcesLinks = useResourcesLinks();

    return (
        <MobileNavMenuPage
            pageLabel={intl.formatMessage({
                id: "globalnav.v2.resources.title",
                defaultMessage: "Resources"
            })}
            cards={resourcesData}
            linksSections={resourcesLinks}
            productKey={productKey}
        />
    );
};
